import { useState } from "react";
import type { CustomerGroup } from "../services/customerGroupsApi";
import {
  useGroupDiscounts,
  useCreateGroupDiscount,
  useDeleteGroupDiscount,
  useSubmitGroupDiscount,
  useApproveGroupDiscount,
  useRejectGroupDiscount,
} from "@/features/discounts/hooks/useDiscounts";
import { ApprovalStatus } from "@/features/discounts/services/discountsApi";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Zap, Trash2, Plus } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface GroupDiscountsDialogProps {
  group: CustomerGroup;
}

export const GroupDiscountsDialog = ({ group }: GroupDiscountsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    campaignName: "",
    description: "",
    discountPercentage: 0,
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  // Inactive (deactivated) campaigns are hidden by default; the toggle reveals them.
  const [showInactive, setShowInactive] = useState(false);

  const discountsQuery = useGroupDiscounts(group.id);
  const createMutation = useCreateGroupDiscount(group.id);
  const deleteMutation = useDeleteGroupDiscount(group.id);
  const submitMutation = useSubmitGroupDiscount(group.id);
  const approveMutation = useApproveGroupDiscount(group.id);
  const rejectMutation = useRejectGroupDiscount(group.id);
  const confirm = useConfirm();
  const notify = useNotification();

  // Inline reject-reason flow: which campaign is being rejected, and the typed reason.
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const allDiscounts = discountsQuery.data ?? [];
  const inactiveCount = allDiscounts.filter((d) => !d.isActive).length;
  const visibleDiscounts = showInactive
    ? allDiscounts
    : allDiscounts.filter((d) => d.isActive);

  const approvalBadge = (status: number): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } => {
    switch (status) {
      case ApprovalStatus.Approved: return { label: "Approved", variant: "default" };
      case ApprovalStatus.PendingApproval: return { label: "Pending approval", variant: "secondary" };
      case ApprovalStatus.Rejected: return { label: "Rejected", variant: "destructive" };
      default: return { label: "Draft", variant: "outline" };
    }
  };

  const handleSubmitForApproval = (discountId: string) => {
    submitMutation.mutate(discountId, {
      onSuccess: () => notify.success("Campaign submitted for approval"),
      onError: (e: any) => notify.error(e?.response?.data?.message ?? "Failed to submit campaign"),
    });
  };

  const handleApprove = (discountId: string) => {
    confirm({
      title: "Approve campaign?",
      description: "Once approved, this campaign discount will apply to matching orders. You cannot approve a campaign you created.",
      confirmLabel: "Approve",
      onConfirm: () =>
        withConfirmSuccess(approveMutation.mutateAsync(discountId), "Campaign approved"),
    });
  };

  const handleReject = (discountId: string) => {
    if (!rejectReason.trim()) {
      notify.error("Enter a rejection reason");
      return;
    }
    rejectMutation.mutate(
      { discountId, rejectionReason: rejectReason.trim() },
      {
        onSuccess: () => {
          notify.success("Campaign rejected");
          setRejectingId(null);
          setRejectReason("");
        },
        onError: (e: any) => notify.error(e?.response?.data?.message ?? "Failed to reject campaign"),
      }
    );
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    // Convert dates to ISO format
    const startDate = new Date(formData.startDate).toISOString();
    const endDate = new Date(formData.endDate).toISOString();

    if (new Date(endDate) <= new Date(startDate)) {
      setFieldErrors({
        endDate: ["End date must be after start date"],
      });
      return;
    }

    createMutation.mutate(
      {
        customerGroupId: group.id,
        campaignName: formData.campaignName,
        description: formData.description,
        discountPercentage: formData.discountPercentage,
        startDate,
        endDate,
      },
      {
        onSuccess: () => {
          setFormData({
            campaignName: "",
            description: "",
            discountPercentage: 0,
            startDate: new Date().toISOString().split("T")[0],
            endDate: new Date().toISOString().split("T")[0],
          });
          setIsCreating(false);
        },
        onError: (error: any) => {
          if (error.response?.data?.errors) {
            setFieldErrors(error.response.data.errors);
          }
        },
      },
    );
  };

  const handleDelete = (discountId: string) => {
    confirm({
      title: "Deactivate discount campaign?",
      description:
        "This campaign will stop applying to orders and be hidden from the list. You can bring it back with “Show inactive”.",
      variant: "destructive",
      confirmLabel: "Deactivate",
      onConfirm: () =>
        withConfirmSuccess(
          deleteMutation.mutateAsync(discountId),
          "Discount campaign deactivated"
        ),
    });
  };

  // const isDiscountActive = (discount: any) => {
  //   const now = new Date();
  //   const start = new Date(discount.startDate);
  //   const end = new Date(discount.endDate);
  //   return now >= start && now <= end;
  // };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Zap className="w-4 h-4" />
          Discount Campaigns
        </button>
      </DialogTrigger>
      <DialogContent
        
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]"
      >
        <DialogHeader>
          <DialogTitle>Discount Campaigns</DialogTitle>
          <DialogDescription>
            Manage discount campaigns for {group.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Discounts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Campaigns</h3>
              <div className="flex items-center gap-2">
                {inactiveCount > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowInactive((v) => !v)}
                  >
                    {showInactive
                      ? "Hide inactive"
                      : `Show inactive (${inactiveCount})`}
                  </Button>
                )}
                {!isCreating && (
                  <Button
                    size="sm"
                    onClick={() => setIsCreating(true)}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Campaign
                  </Button>
                )}
              </div>
            </div>

            {discountsQuery.isLoading ? (
              <LoadingAnimation text="Loading campaigns..." />
            ) : visibleDiscounts.length > 0 ? (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign Name</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleDiscounts.map((discount) => (
                      <TableRow key={discount.id} className={discount.isActive ? undefined : "opacity-60"}>
                        <TableCell className="font-medium">
                          {discount.campaignName}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {discount.discountPercentage}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(discount.startDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(discount.endDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {(() => {
                              const b = approvalBadge(discount.approvalStatus);
                              return <Badge variant={b.variant}>{b.label}</Badge>;
                            })()}
                            {!discount.isActive && (
                              <Badge variant="outline" className="border-gray-400 text-gray-500">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          {discount.approvalStatus === ApprovalStatus.Rejected && discount.rejectionReason && (
                            <p className="text-xs text-muted-foreground mt-1">{discount.rejectionReason}</p>
                          )}
                          {!discount.isActive ? (
                            <p className="text-xs text-gray-500 mt-1">Deactivated — not applied to orders</p>
                          ) : discount.approvalStatus !== ApprovalStatus.Approved ? (
                            <p className="text-xs text-amber-600 mt-1">Not yet applied to orders</p>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          {rejectingId === discount.id ? (
                            <div className="flex items-center justify-end gap-2">
                              <Input
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Rejection reason"
                                className="h-8 w-44"
                              />
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject(discount.id)}
                                disabled={rejectMutation.isPending}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setRejectingId(null); setRejectReason(""); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              {discount.approvalStatus === ApprovalStatus.Draft && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSubmitForApproval(discount.id)}
                                  disabled={submitMutation.isPending}
                                >
                                  Submit for approval
                                </Button>
                              )}
                              {discount.approvalStatus === ApprovalStatus.PendingApproval && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleApprove(discount.id)}
                                    disabled={approveMutation.isPending}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setRejectingId(discount.id); setRejectReason(""); }}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(discount.id)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : inactiveCount > 0 ? (
              <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                No active campaigns.{" "}
                <button
                  type="button"
                  className="text-primary underline underline-offset-2"
                  onClick={() => setShowInactive(true)}
                >
                  Show {inactiveCount} inactive
                </button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                No discount campaigns configured
              </div>
            )}
          </div>

          {/* Create Form */}
          {isCreating && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-4">Create New Campaign</h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                {createMutation.error && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {createMutation.error.message}
                  </div>
                )}

                <Field>
                  <FieldLabel htmlFor="campaignName">
                    Campaign Name <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="campaignName"
                    value={formData.campaignName}
                    onChange={(e) =>
                      handleChange("campaignName", e.target.value)
                    }
                    placeholder="e.g., Summer Sale 2026"
                    required
                    disabled={createMutation.isPending}
                  />
                  {fieldErrors.campaignName && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.campaignName.join(", ")}
                    </div>
                  )}
                </Field>

                <Field>
                  <FieldLabel htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </FieldLabel>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    placeholder="Campaign details and terms"
                    required
                    disabled={createMutation.isPending}
                    className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                  {fieldErrors.description && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.description.join(", ")}
                    </div>
                  )}
                </Field>

                <Field>
                  <FieldLabel htmlFor="discountPercentage">
                    Discount Percentage <span className="text-red-500">*</span>
                  </FieldLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      id="discountPercentage"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.discountPercentage}
                      onChange={(e) =>
                        handleChange(
                          "discountPercentage",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      required
                      disabled={createMutation.isPending}
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  {fieldErrors.discountPercentage && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.discountPercentage.join(", ")}
                    </div>
                  )}
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  <Field>
                    <FieldLabel htmlFor="startDate">
                      Start Date <span className="text-red-500">*</span>
                    </FieldLabel>
                    <DatePicker
                      id="startDate"
                      value={formData.startDate}
                      onChange={(value) => handleChange("startDate", value)}
                      required
                      disabled={createMutation.isPending}
                    />
                    {fieldErrors.startDate && (
                      <div className="text-red-500 text-xs mt-1">
                        {fieldErrors.startDate.join(", ")}
                      </div>
                    )}
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="endDate">
                      End Date <span className="text-red-500">*</span>
                    </FieldLabel>
                    <DatePicker
                      id="endDate"
                      value={formData.endDate}
                      onChange={(value) => handleChange("endDate", value)}
                      required
                      disabled={createMutation.isPending}
                    />
                    {fieldErrors.endDate && (
                      <div className="text-red-500 text-xs mt-1">
                        {fieldErrors.endDate.join(", ")}
                      </div>
                    )}
                  </Field>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending
                      ? "Creating..."
                      : "Create Campaign"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreating(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
